import React, { FC, InputHTMLAttributes } from "react";
import { useField } from "formik";
import styled from "styled-components";

import { Asterisk } from "./Asterisk";
import { FormError } from "./FormError";
import { Label } from "./Label";
import { Switch } from "./Switch";

interface Props extends InputHTMLAttributes<HTMLDivElement> {
  name: string;
  center?: boolean;
  horizontal?: boolean;
  label?: string;
  labelStyles?: React.CSSProperties;
  active?: boolean;
  resetForm?: () => void;
}

const Container = styled.div<{ center?: boolean; horizontal?: boolean }>`
  display: flex;
  flex-direction: ${({ horizontal }) => (horizontal ? "row" : "column")};
  align-items: ${({ center, horizontal }) => {
    if (horizontal) return "center";
    return center ? "center" : "flex-start";
  }};
  justify-content: ${({ horizontal }) =>
    horizontal ? "space-between" : "flex-start"};
  margin-top: ${({ theme }) => theme.smallestPadding};
  width: 100%;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const SwitchField: FC<Props> = ({
  name,
  center,
  horizontal,
  label,
  labelStyles,
  id,
  active = false,
  required,
  disabled,
  ...props
}) => {
  const [field, , { setValue }] = useField(name);

  return (
    <Container center={center} horizontal={horizontal}>
      {label && (
        <LabelContainer>
          <Label
            className="mb-6"
            htmlFor={id ?? name}
            style={labelStyles}
            children={label}
          />
          {required && <Asterisk />}
        </LabelContainer>
      )}
      <Switch
        id={id ?? name}
        active={field.value}
        data-testid="switch"
        data-teststate={field.value ? "active" : "inactive"}
        {...field}
        {...props}
        onClick={() => {
          if (disabled) return;
          setValue(!field.value);
          if (props.resetForm !== undefined) {
            props.resetForm();
            setValue(!field.value);
          }
        }}
        disabled={disabled}
      />
      <FormError name={name} />
    </Container>
  );
};
